import React, { useState, useEffect, useCallback } from 'react';
import { DollarSign, TrendingUp, Calendar, BookOpen, Clock, BarChart3, Users, CheckCircle } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { getAuthToken } from '../utils/auth';

interface FinancialStats {
  totalEarnings: number;
  thisMonth: number;
  thisWeek: number;
  completedSessions: number;
  averagePerSession: number;
  totalHours: number;
  earningsBySubject: Array<{
    subject: string;
    earnings: number;
    sessions: number;
  }>;
  monthlyEarnings: Array<{
    month: string;
    earnings: number;
    sessions: number;
  }>;
}

const FinancialDashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<FinancialStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTimeframe, setSelectedTimeframe] = useState<'week' | 'month' | 'year'>('month');

  const fetchFinancialData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = getAuthToken();
      if (!token) {
        setError('Authentication required. Please log in again.');
        return;
      }

      const response = await api.get('/admin/financial-stats', {
        params: { timeframe: selectedTimeframe }
      });
      
      if (response.data) {
        setStats(response.data);
      } else {
        setError('No financial data available.');
      }
    } catch (error: any) {
      console.error('Failed to fetch financial data:', error);
      
      if (error.code === 'ECONNABORTED') {
        setError('Request timed out. Please check your connection and try again.');
      } else if (error.response?.status === 401) {
        setError('Authentication expired. Please log in again.');
      } else if (error.response?.status === 403) {
        setError('Access denied. Tutor privileges required.');
      } else if (error.response?.status >= 500) {
        setError('Server error. Please try again later.');
      } else {
        setError('Failed to load financial data. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedTimeframe]);

  useEffect(() => {
    if (user?.role === 'TUTOR') {
      fetchFinancialData().catch(error => {
        console.error('Error in fetchFinancialData effect:', error);
      });
    }
  }, [user, selectedTimeframe, fetchFinancialData]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const formatHours = (hours: number) => {
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours % 1) * 60);
    return minutes > 0 ? `${wholeHours}h ${minutes}m` : `${wholeHours}h`;
  };

  if (!user || user.role !== 'TUTOR') {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-cream border border-rule rounded-card p-4 flex items-center">
          <DollarSign className="w-5 h-5 text-ochre mr-3" />
          <p className="text-ink">Access denied. Tutor role required.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-cream rounded w-1/4 mb-6"></div>
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-cream rounded"></div>
            ))}
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="h-64 bg-cream rounded"></div>
            <div className="h-64 bg-cream rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-card p-4">
          <p className="text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <DollarSign className="w-12 h-12 text-muted mx-auto mb-4" />
          <p className="text-ink">No financial data available yet.</p>
          <p className="text-sm text-muted mt-1">Complete some payment confirmations to see statistics!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-ink mb-4">Payment Dashboard</h2>
            <p className="text-muted">
              Track confirmed payments and session statistics.
            </p>
          </div>
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value as 'week' | 'month' | 'year')}
            className="input"
          >
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="year">This Year</option>
          </select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-paper border border-rule rounded-card p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-emerald-100 rounded-card flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-emerald-700" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-muted">Total Payments Confirmed</p>
              <p className="text-2xl font-bold text-ink">{formatCurrency(stats.totalEarnings)}</p>
            </div>
          </div>
        </div>

        <div className="bg-paper border border-rule rounded-card p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-ochre/15 rounded-card flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-ochre" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-muted">
                {selectedTimeframe === 'week' ? 'This Week' :
                 selectedTimeframe === 'month' ? 'This Month' : 'This Year'}
              </p>
              <p className="text-2xl font-bold text-ink">
                {formatCurrency(
                  selectedTimeframe === 'week' ? stats.thisWeek :
                  selectedTimeframe === 'month' ? stats.thisMonth :
                  stats.totalEarnings
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-paper border border-rule rounded-card p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-navy/15 rounded-card flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-navy" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-muted">Payments Confirmed</p>
              <p className="text-2xl font-bold text-ink">{stats.completedSessions}</p>
            </div>
          </div>
        </div>

        <div className="bg-paper border border-rule rounded-card p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-ochre/25 rounded-card flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-ochre" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-muted">Average per Payment</p>
              <p className="text-2xl font-bold text-ink">{formatCurrency(stats.averagePerSession)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Stats */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="bg-paper border border-rule rounded-card p-6">
          <div className="flex items-center mb-4">
            <Clock className="w-5 h-5 text-muted mr-2" />
            <h3 className="text-lg font-semibold text-ink">Teaching Hours</h3>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-ink mb-2">{formatHours(stats.totalHours)}</p>
            <p className="text-sm text-muted">Total hours taught</p>
            <div className="mt-4 p-3 bg-cream border border-rule rounded-card">
              <p className="text-sm text-ink">
                <span className="font-medium">Hourly Rate:</span> {formatCurrency(stats.totalEarnings / stats.totalHours || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-paper border border-rule rounded-card p-6">
          <div className="flex items-center mb-4">
            <Users className="w-5 h-5 text-muted mr-2" />
            <h3 className="text-lg font-semibold text-ink">Quick Stats</h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-muted">Sessions per week</span>
              <span className="text-sm font-medium text-ink">
                {Math.round(stats.completedSessions / 4) || 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted">Hours per week</span>
              <span className="text-sm font-medium text-ink">
                {Math.round((stats.totalHours / 4) * 10) / 10 || 0}h
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted">Weekly payments</span>
              <span className="text-sm font-medium text-ink">
                {formatCurrency(stats.totalEarnings / 4 || 0)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Earnings by Subject */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-paper border border-rule rounded-card p-6">
          <div className="flex items-center mb-6">
            <BookOpen className="w-5 h-5 text-muted mr-2" />
            <h3 className="text-lg font-semibold text-ink">Payments by Subject</h3>
          </div>

          {!Array.isArray(stats.earningsBySubject) || stats.earningsBySubject.length === 0 ? (
            <div className="text-center py-8">
              <BookOpen className="w-12 h-12 text-muted mx-auto mb-4" />
              <p className="text-muted">No subject data available yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {stats.earningsBySubject.map((subject, index) => (
                <div key={index} className="border border-rule rounded-card p-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-ink">{subject.subject}</h4>
                    <span className="text-sm font-medium text-muted">
                      {subject.sessions} sessions
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="w-full bg-cream rounded-full h-2 mr-4">
                      <div
                        className="bg-ochre h-2 rounded-full"
                        style={{
                          width: `${(subject.earnings / stats.totalEarnings) * 100}%`
                        }}
                      ></div>
                    </div>
                    <span className="text-lg font-semibold text-ink">
                      {formatCurrency(subject.earnings)}
                    </span>
                  </div>
                  <p className="text-xs text-muted mt-1">
                    {((subject.earnings / stats.totalEarnings) * 100).toFixed(1)}% of total payments
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Monthly Earnings Trend */}
        <div className="bg-paper border border-rule rounded-card p-6">
          <div className="flex items-center mb-6">
            <Calendar className="w-5 h-5 text-muted mr-2" />
            <h3 className="text-lg font-semibold text-ink">Monthly Trends</h3>
          </div>

          {!Array.isArray(stats.monthlyEarnings) || stats.monthlyEarnings.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="w-12 h-12 text-muted mx-auto mb-4" />
              <p className="text-muted">No monthly data available yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {Array.isArray(stats.monthlyEarnings) ? stats.monthlyEarnings.slice(-6).map((month, index) => (
                <div key={index} className="border border-rule rounded-card p-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-ink">{month.month}</h4>
                    <span className="text-sm font-medium text-muted">
                      {month.sessions} sessions
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="w-full bg-cream rounded-full h-2 mr-4">
                      <div
                        className="bg-emerald-600 h-2 rounded-full"
                        style={{
                          width: `${Math.max((month.earnings / Math.max(...(Array.isArray(stats.monthlyEarnings) ? stats.monthlyEarnings.map(m => m.earnings) : [1]))) * 100, 5)}%`
                        }}
                      ></div>
                    </div>
                    <span className="text-lg font-semibold text-ink">
                      {formatCurrency(month.earnings)}
                    </span>
                  </div>
                </div>
              )) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FinancialDashboard;